import { motion } from "framer-motion";
import {
  GraduationCap, FileCheck2, Briefcase, Award, Plane, Users,
  Phone, Mail, MapPin, Clock, ArrowRight, ShieldCheck, Clock4, Sparkles, BadgeCheck,
} from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import WhatsAppFloat from "@/components/site/WhatsAppFloat";
import EnquiryForm from "@/components/site/EnquiryForm";
import { BRAND, STATS, DESTINATIONS } from "@/lib/brand";

const HERO_IMG = "https://images.unsplash.com/photo-1685401704618-fd8243812ef7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwyfHx1bml2ZXJzaXR5JTIwY2FtcHVzJTIwc3R1ZGVudCUyMGdyYWR1YXRpb258ZW58MHx8fHwxNzgyNTY5NTAwfDA&ixlib=rb-4.1.0&q=85";
const ABOUT_IMG = "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBvZmZpY2UlMjBtZWV0aW5nJTIwY29uc3VsdGluZ3xlbnwwfHx8fDE3ODI1Njk1MDB8MA&ixlib=rb-4.1.0&q=85";

const SERVICES = [
  { icon: GraduationCap, title: "University Admissions", desc: "Shortlisting, applications & offers from 700+ partner universities." },
  { icon: FileCheck2, title: "Visa Processing", desc: "End-to-end student visa filing with high approval rates." },
  { icon: Users, title: "Career Counselling", desc: "Free & genuine advice from professionals settled abroad." },
  { icon: Award, title: "Scholarship Guidance", desc: "Maximise funding with our scholarship facilitation." },
  { icon: Briefcase, title: "On / Off Campus Jobs", desc: "Part-time work and internship guidance while you study." },
  { icon: Plane, title: "Work & Settlement Visa", desc: "Post-study work and long-term settlement advisory." },
];

const WHY = [
  { icon: Clock4, title: "1 Month Easy Process", desc: "Fast-tracked admissions with minimal paperwork." },
  { icon: BadgeCheck, title: "No IELTS Options", desc: "Admission pathways without IELTS (conditions apply)." },
  { icon: Sparkles, title: "Scholarship Facilities", desc: "Dedicated support to secure the best scholarships." },
  { icon: ShieldCheck, title: "Free & Genuine Advice", desc: "Honest guidance from settled persons — no false promises." },
];

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08 } }),
};

const VIEWPORT = { once: true };
const MARQUEE_SETS = ["set-a", "set-b"];

const Heading = ({ over, title, sub, light }) => (
  <div className="max-w-2xl mx-auto text-center mb-14">
    <span className="text-xs font-bold tracking-[0.25em] uppercase text-gold">{over}</span>
    <h2 className={`font-display text-3xl sm:text-4xl font-bold tracking-tight mt-3 ${light ? "text-white" : "text-navy"}`}>{title}</h2>
    {sub && <p className={`mt-4 ${light ? "text-white/70" : "text-slate-600"}`}>{sub}</p>}
  </div>
);

export default function Landing() {
  return (
    <div className="overflow-x-hidden">
      <Navbar />
      <WhatsAppFloat />

      {/* HERO */}
      <section id="home" className="relative min-h-[100svh] flex items-center">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Students graduating abroad" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-navy-dark via-navy/90 to-navy/50" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 md:px-10 pt-28 pb-16 w-full">
          <motion.div initial="hidden" animate="show" variants={fadeUp} className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-gold/15 border border-gold/30 text-gold px-4 py-1.5 text-xs font-bold tracking-wide">
              <span className="w-2 h-2 rounded-full bg-gold animate-pulse" /> ADMISSIONS ARE OPEN · 10+2 / UG / Diploma
            </span>
            <motion.h1 variants={fadeUp} custom={1} className="font-display text-4xl sm:text-6xl lg:text-7xl font-extrabold text-white tracking-tight mt-6 leading-[1.05]">
              Your Gateway to <span className="text-gold">Global Education</span>
            </motion.h1>
            <motion.p variants={fadeUp} custom={2} className="text-lg sm:text-xl text-white/80 mt-6 max-w-xl">
              Study in USA, UK, Canada, Germany, Australia, Italy & 29+ countries. Trusted guidance, scholarships and visa support — from start to settlement.
            </motion.p>
            <motion.div variants={fadeUp} custom={3} className="flex flex-wrap gap-4 mt-9">
              <a href="#enquiry" data-testid="hero-cta" className="rounded-full bg-gold hover:bg-gold-dark text-white px-8 py-4 font-bold shadow-xl shadow-gold/30 active:scale-95 transition-all">
                Get Free Consultation
              </a>
              <a href="#destinations" data-testid="hero-explore" className="rounded-full border-2 border-white/40 hover:border-gold hover:text-gold text-white px-8 py-4 font-bold transition-all flex items-center gap-2">
                Explore Destinations <ArrowRight className="w-4 h-4" />
              </a>
            </motion.div>
          </motion.div>
        </div>
        {/* marquee */}
        <div className="absolute bottom-0 inset-x-0 bg-navy-dark/80 backdrop-blur border-t border-white/10 py-3 overflow-hidden">
          <div className="marquee text-white/70 text-sm font-semibold tracking-wide">
            {MARQUEE_SETS.map((mk) => (
              <span key={mk}>
                {["USA", "UK", "CANADA", "GERMANY", "AUSTRALIA", "ITALY", "IRELAND", "FRANCE", "29+ COUNTRIES"].map((c) => (
                  <span key={c} className="mx-6">★ {c}</span>
                ))}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-white py-14 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {STATS.map((s, i) => (
            <motion.div key={s.label} initial="hidden" whileInView="show" viewport={VIEWPORT} variants={fadeUp} custom={i} className="text-center">
              <div className="font-display text-4xl sm:text-5xl font-extrabold text-navy">{s.value}</div>
              <div className="text-sm text-slate-500 mt-1 font-medium">{s.label}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* DESTINATIONS */}
      <section id="destinations" className="py-20 md:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <Heading over="Study Destinations" title="Where do you want to study?" sub="Top global destinations with world-class universities and bright career prospects." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {DESTINATIONS.map((d, i) => (
              <motion.a
                key={d.name}
                href="#enquiry"
                initial="hidden" whileInView="show" viewport={VIEWPORT} variants={fadeUp} custom={i % 3}
                data-testid={`destination-${d.name.toLowerCase().replace(/\s/g, "-")}`}
                className="group relative rounded-2xl overflow-hidden h-72 shadow-md hover:shadow-2xl hover:-translate-y-2 transition-all duration-300"
              >
                <img src={d.img} alt={d.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-dark via-navy-dark/40 to-transparent" />
                <div className="absolute bottom-0 p-6 text-white">
                  <h3 className="font-display text-2xl font-bold">{d.name}</h3>
                  <p className="text-white/80 text-sm mt-1">{d.blurb}</p>
                  <span className="inline-flex items-center gap-1 text-gold font-semibold text-sm mt-3 group-hover:gap-2 transition-all">
                    Explore <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <Heading over="Our Services" title="Complete support, end to end" sub="From your first counselling session to settling abroad — we are with you at every step." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => (
              <motion.div
                key={s.title}
                initial="hidden" whileInView="show" viewport={VIEWPORT} variants={fadeUp} custom={i % 3}
                data-testid={`service-${i}`}
                className="rounded-2xl border border-slate-200 hover:border-gold p-7 hover:shadow-lg transition-all duration-300 group"
              >
                <span className="grid place-items-center w-14 h-14 rounded-2xl bg-navy/5 text-navy group-hover:bg-gold group-hover:text-white transition-colors mb-5">
                  <s.icon className="w-7 h-7" />
                </span>
                <h3 className="font-display text-xl font-bold text-navy">{s.title}</h3>
                <p className="text-slate-600 text-sm mt-2 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section id="why" className="py-20 md:py-28 bg-navy relative">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <Heading over="Why Choose Us" title="The Invicta advantage" light sub="" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {WHY.map((w, i) => (
              <motion.div
                key={w.title}
                initial="hidden" whileInView="show" viewport={VIEWPORT} variants={fadeUp} custom={i}
                data-testid={`why-${i}`}
                className="rounded-2xl bg-white/5 border border-white/10 p-7 hover:bg-white/10 transition-colors"
              >
                <w.icon className="w-9 h-9 text-gold mb-4" />
                <h3 className="font-display text-lg font-bold text-white">{w.title}</h3>
                <p className="text-white/60 text-sm mt-2">{w.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-14 items-center">
          <motion.div initial="hidden" whileInView="show" viewport={VIEWPORT} variants={fadeUp} className="relative">
            <img src={ABOUT_IMG} alt="Invicta counselling" className="rounded-3xl shadow-2xl w-full object-cover" />
            <div className="absolute -bottom-6 -right-2 sm:right-6 bg-gold text-white rounded-2xl px-6 py-5 shadow-xl">
              <div className="font-display text-3xl font-extrabold">99%</div>
              <div className="text-xs font-semibold tracking-wide">Placement Success</div>
            </div>
          </motion.div>
          <motion.div initial="hidden" whileInView="show" viewport={VIEWPORT} variants={fadeUp} custom={1}>
            <span className="text-xs font-bold tracking-[0.25em] uppercase text-gold">About Us</span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-navy tracking-tight mt-3">A trusted name in overseas education</h2>
            <p className="text-slate-600 mt-5 leading-relaxed">
              Invicta Overseas is a trusted educational consultancy helping students achieve their dream of studying abroad.
              We provide admission guidance, visa processing, counselling and complete support for students planning to study
              in the USA, UK, Canada, Germany, Australia, Italy and 29+ other countries.
            </p>
            <p className="text-slate-600 mt-4 leading-relaxed">
              With 700+ partner universities and 1000+ students assisted, our advice is free, genuine and comes from
              professionals who have themselves settled abroad.
            </p>
            <div className="grid grid-cols-2 gap-4 mt-8">
              {["700+ Partner Universities", "1000+ Students Assisted", "29+ Countries", "No IELTS Pathways"].map((t) => (
                <div key={t} className="flex items-center gap-2 text-navy font-semibold text-sm">
                  <BadgeCheck className="w-5 h-5 text-gold shrink-0" /> {t}
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ENQUIRY + CONTACT */}
      <section id="enquiry" className="py-20 md:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-12">
          <div id="contact">
            <span className="text-xs font-bold tracking-[0.25em] uppercase text-gold">Get In Touch</span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-navy tracking-tight mt-3">Start your journey today</h2>
            <p className="text-slate-600 mt-4">Fill the form or reach us directly. Free registration — just share your details and our counsellor will call you back.</p>

            <div className="space-y-4 mt-8">
              <a href={`tel:${BRAND.phoneRaw}`} data-testid="contact-phone" className="flex items-center gap-4 bg-white rounded-2xl p-4 border border-slate-200 hover:border-gold transition-colors">
                <span className="grid place-items-center w-12 h-12 rounded-xl bg-navy text-gold"><Phone className="w-5 h-5" /></span>
                <div><div className="text-xs text-slate-500">Call us</div><div className="font-bold text-navy">{BRAND.phone}</div></div>
              </a>
              <a href={`mailto:${BRAND.email}`} data-testid="contact-email" className="flex items-center gap-4 bg-white rounded-2xl p-4 border border-slate-200 hover:border-gold transition-colors">
                <span className="grid place-items-center w-12 h-12 rounded-xl bg-navy text-gold"><Mail className="w-5 h-5" /></span>
                <div><div className="text-xs text-slate-500">Email us</div><div className="font-bold text-navy">{BRAND.email}</div></div>
              </a>
              <div className="flex items-center gap-4 bg-white rounded-2xl p-4 border border-slate-200">
                <span className="grid place-items-center w-12 h-12 rounded-xl bg-navy text-gold"><MapPin className="w-5 h-5" /></span>
                <div><div className="text-xs text-slate-500">Visit us</div><div className="font-bold text-navy">Hyderabad, Telangana, India</div></div>
              </div>
              <div className="flex items-start gap-4 bg-white rounded-2xl p-4 border border-slate-200">
                <span className="grid place-items-center w-12 h-12 rounded-xl bg-navy text-gold shrink-0"><Clock className="w-5 h-5" /></span>
                <div className="w-full">
                  <div className="text-xs text-slate-500 mb-1">Office Hours</div>
                  {BRAND.hours.map((h) => (
                    <div key={h.d} className="flex justify-between text-sm"><span className="text-navy font-medium">{h.d}</span><span className="text-slate-500">{h.t}</span></div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div>
            <EnquiryForm />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
